import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Send, Loader2, CheckCircle2 } from "lucide-react";

const TOPICS = [
  { code: "general",     label: "General question" },
  { code: "feature",     label: "Feature request" },
  { code: "bug",         label: "Bug report" },
  { code: "partnership", label: "Partnership / design partner" },
  { code: "press",       label: "Press / media" },
  { code: "other",       label: "Other" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    topic: "general",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return toast.error("Please fill out name, email, and message");
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll reply within one business day");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please email hello@qualmitta.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900">
      <Navbar />
      <div className="mx-auto grid max-w-[1200px] gap-12 px-6 py-16 lg:grid-cols-12 lg:px-10 lg:py-24">
        <div className="lg:col-span-5">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 overline text-slate-500 hover:text-slate-900">
            <ArrowLeft className="h-3 w-3" /> Back home
          </Link>
          <div className="overline mb-3 text-[#2563EB]">CONTACT</div>
          <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-tight lg:text-6xl">
            Say hello.
          </h1>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-slate-600">
            Questions, feature requests, partnerships, or a regulatory workflow
            we should support next? We reply personally, usually within one
            business day.
          </p>

          <div className="mt-10 space-y-6">
            <div className="border border-slate-200 bg-white p-6">
              <div className="mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#2563EB]" />
                <div className="overline text-[#2563EB]">EMAIL US DIRECTLY</div>
              </div>
              <a
                href="mailto:hello@qualmitta.com"
                data-testid="contact-email-link"
                className="group inline-flex items-center gap-2 font-serif text-2xl font-semibold text-slate-900 hover:text-[#93C5FD]"
              >
                hello@qualmitta.com
                <span className="translate-y-[-2px] transition-transform group-hover:translate-x-1">↗</span>
              </a>
            </div>

            <div className="border border-slate-200 bg-white p-6">
              <div className="overline mb-3 text-slate-500">WHAT WE'RE GOOD AT ANSWERING</div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>· "Does Qualmitta support &lt;insert workflow&gt;?"</li>
                <li>· "Can you add EU MDR annex X screening?"</li>
                <li>· "I'm building an FDA-regulated &lt;X&gt; — is this useful?"</li>
                <li>· "We'd love to be a design partner."</li>
                <li>· "Something broke."</li>
              </ul>
            </div>

            <div className="overline text-slate-400">
              Qualmitta is a drafting and research aid. Not legal advice. Not a substitute for qualified counsel.
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <div className="border border-emerald-500/40 bg-emerald-500/8 p-10">
              <CheckCircle2 className="mb-4 h-10 w-10 text-emerald-400" strokeWidth={1.5} />
              <h2 className="font-serif text-3xl font-semibold text-slate-900">Message sent.</h2>
              <p className="mt-3 max-w-md text-slate-600">
                We got it and will reply to <b className="text-slate-900">{form.email}</b> within one business day.
                If it's urgent, email <a href="mailto:hello@qualmitta.com" className="text-[#93C5FD] underline">hello@qualmitta.com</a> directly.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => { setSent(false); setForm({ name: "", email: "", company: "", topic: "general", message: "", website: "" }); }}
                  data-testid="contact-send-another"
                  className="border border-slate-300 px-4 py-2 text-sm font-medium hover:border-[#2563EB]"
                >
                  Send another
                </button>
                <Link to="/" className="border border-slate-200 px-4 py-2 text-sm font-medium hover:border-slate-300">
                  Back home
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-slate-200 bg-white p-6 lg:p-10"
            >
              <div className="overline mb-6 text-[#2563EB]">SEND A MESSAGE</div>

              {/* Honeypot — hidden from users, bots often fill it */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
              >
                <label>
                  Website (leave empty)
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    data-testid="contact-honeypot"
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="overline mb-2 block text-slate-500">Your name *</label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Alex Chen"
                    required
                    className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="overline mb-2 block text-slate-500">Email *</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@company.com"
                    required
                    className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="overline mb-2 block text-slate-500">Company (optional)</label>
                  <input
                    data-testid="contact-company"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Acme Medical Devices"
                    className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="overline mb-2 block text-slate-500">Topic</label>
                  <select
                    data-testid="contact-topic"
                    value={form.topic}
                    onChange={(e) => update("topic", e.target.value)}
                    className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 focus:border-[#2563EB] focus:outline-none"
                  >
                    {TOPICS.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="overline mb-2 block text-slate-500">Your message *</label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={8}
                  required
                  maxLength={8000}
                  placeholder="Tell us what's on your mind — device or therapeutic you're building, workflow you wish existed, standard you want covered next, or just say hi."
                  className="w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
                />
                <div className="overline mt-1 text-right text-slate-300">{form.message.length} / 8000</div>
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={sending}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send message
              </button>

              <p className="overline mt-4 leading-relaxed text-slate-400">
                We use your info only to respond. No newsletters, no lists. Full details in our{" "}
                <Link to="/privacy" className="text-slate-500 hover:text-slate-900">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
